// frontend/src/components/avatarupload/AvatarUpload.jsx
//
// The photo control: picker, upload, removal gate and busy state. The server
// is the only validator — it owns the size and format bounds
// (authentication/views/user_profile.py) and refuses with a structured code
// this component translates. A second copy of those bounds here could only
// ever disagree with it.
//
// Outcomes go to the caller, which owns the one place this page talks to the
// merchant: `onAvatarChange(url | null)` on success, `onError(message)` with
// an already-translated line on failure.

import { useState, useRef } from 'react';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ICON, Camera, Trash, UserCircle } from '../../icons';
import { sessionService } from '../../services/SessionApiService';
import { translateRequestError } from '../../queries/api';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../common/ConfirmDialog';

import './AvatarUpload.css';

// Narrows the file picker to what the server decodes. A hint, not a rule:
// whatever arrives is still the server's to accept or refuse.
const PICKER_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp';

const AvatarUpload = ({ currentAvatar, onAvatarChange, onError, size = 48 }) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { confirm, dialogProps } = useConfirmDialog();

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];

    // Reset the input immediately
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (!file) return;

    try {
      setUploading(true);
      const result = await sessionService.uploadAvatar(file);
      onAvatarChange(result.avatar_url);
    } catch (error) {
      console.error('uploadAvatar failed:', error.message);
      onError(translateRequestError(error, t, 'userProfile.avatarUploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!currentAvatar) return;

    const ok = await confirm({
      title: t('userProfile.removePhotoTitle'),
      message: t('userProfile.removePhotoConfirm'),
      confirmLabel: t('userProfile.removePhoto'),
      variant: 'danger',
    });
    if (!ok) return;

    try {
      setUploading(true);
      await sessionService.deleteAvatar();
      onAvatarChange(null);
    } catch (error) {
      console.error('deleteAvatar failed:', error.message);
      onError(translateRequestError(error, t, 'userProfile.avatarRemoveFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleCameraClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Dynamic size must be inline — driven by prop
  const sizeStyle = { width: size, height: size };

  return (
    <div className="avatar-upload-container" style={sizeStyle}>
      <div className="avatar-image-wrapper" style={sizeStyle}>
        {currentAvatar ? (
          <img
            src={currentAvatar}
            alt=""
            key={currentAvatar}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.querySelector('.avatar-fallback-icon')?.style.removeProperty('display');
            }}
          />
        ) : null}
        <UserCircle
          className="avatar-fallback-icon"
          size={size}
          style={currentAvatar ? { display: 'none' } : undefined}
        />
      </div>

      {uploading && (
        <div className="avatar-upload-loading">
          <CircularProgress size={24} sx={{ color: 'white' }} />
        </div>
      )}

      <div className="avatar-upload-actions">
        <input
          ref={fileInputRef}
          type="file"
          accept={PICKER_ACCEPT}
          onChange={handleFileSelect}
          className="hidden-input"
          id="avatar-file-input"
        />

        {currentAvatar && (
          <Tooltip title={t('userProfile.removePhoto')}>
            <IconButton
              size="small"
              onClick={handleDelete}
              disabled={uploading}
              className="avatar-delete-button"
            >
              <Trash size={ICON.xl} />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title={t('userProfile.changePhoto')}>
          <IconButton
            size="small"
            onClick={handleCameraClick}
            disabled={uploading}
            className="avatar-upload-button"
          >
            <Camera size={ICON.xl} />
          </IconButton>
        </Tooltip>
      </div>

      <ConfirmDialog {...dialogProps} />
    </div>
  );
};

export default AvatarUpload;
